<script setup>
import { computed, onMounted, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import StudioBadge from './StudioBadge.vue';
import StudioEmptyState from './StudioEmptyState.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const records = useMapGetter('aiBotDocuments/getRecords');
const uiFlags = useMapGetter('aiBotDocuments/getUIFlags');
const deleteDialogRef = ref(null);
const selectedDocument = ref({});

const tableHeaders = computed(() => [
  t('AI_AGENT.KNOWLEDGE.TABLE.HEADERS.NAME'),
  t('AI_AGENT.KNOWLEDGE.TABLE.HEADERS.STATUS'),
  t('AI_AGENT.KNOWLEDGE.TABLE.HEADERS.CHUNKS'),
  t('AI_AGENT.KNOWLEDGE.TABLE.HEADERS.INDEXED_AT'),
  t('AI_AGENT.KNOWLEDGE.TABLE.HEADERS.ACTIONS'),
]);

const statusLabel = status => {
  if (status === 'processing')
    return t('AI_AGENT.KNOWLEDGE.TABLE.STATUS.PROCESSING');
  if (status === 'ready') return t('AI_AGENT.KNOWLEDGE.TABLE.STATUS.READY');
  if (status === 'failed') return t('AI_AGENT.KNOWLEDGE.TABLE.STATUS.FAILED');
  return t('AI_AGENT.KNOWLEDGE.TABLE.STATUS.PENDING');
};

const STATUS_TONES = {
  ready: 'teal',
  processing: 'blue',
  failed: 'ruby',
};

const statusTone = status => STATUS_TONES[status] || 'slate';

const isBusy = document =>
  document.status === 'pending' || document.status === 'processing';

const formatDate = value => (value ? new Date(value).toLocaleString() : '—');

const refresh = () => {
  store.dispatch('aiBotDocuments/get', { botId: props.bot.id });
};

const reindex = async document => {
  try {
    await store.dispatch('aiBotDocuments/reindex', {
      botId: props.bot.id,
      id: document.id,
    });
    useAlert(t('AI_AGENT.KNOWLEDGE.TABLE.REINDEX_SUCCESS'));
  } catch (error) {
    useAlert(t('AI_AGENT.KNOWLEDGE.TABLE.REINDEX_ERROR'));
  }
};

const openDeleteDialog = document => {
  selectedDocument.value = document;
  deleteDialogRef.value.open();
};

const confirmDelete = async () => {
  try {
    await store.dispatch('aiBotDocuments/delete', {
      botId: props.bot.id,
      id: selectedDocument.value.id,
    });
    useAlert(t('AI_AGENT.KNOWLEDGE.TABLE.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('AI_AGENT.KNOWLEDGE.TABLE.DELETE_ERROR'));
  } finally {
    selectedDocument.value = {};
    deleteDialogRef.value.close();
  }
};

onMounted(refresh);

defineExpose({ refresh });
</script>

<template>
  <div class="flex flex-col gap-2">
    <StudioEmptyState
      v-if="!records.length && !uiFlags.isFetching"
      icon="i-lucide-book-open"
      :title="t('AI_AGENT.KNOWLEDGE.TABLE.EMPTY')"
      :description="t('AI_AGENT.KNOWLEDGE.TABLE.EMPTY_HELP')"
      data-testid="documents-empty"
    />
    <BaseTable
      v-else
      :headers="tableHeaders"
      :items="records"
      :loading="uiFlags.isFetching"
      data-testid="documents-table"
    >
      <template #row="{ items }">
        <BaseTableRow
          v-for="document in items"
          :key="document.id"
          :item="document"
        >
          <template #default>
            <BaseTableCell class="max-w-0">
              <span class="flex items-center min-w-0 gap-2.5">
                <span
                  class="flex items-center justify-center rounded-lg shrink-0 size-8 bg-n-alpha-2 text-n-slate-11"
                >
                  <span class="i-lucide-file-text size-4" />
                </span>
                <span class="block truncate text-body-main text-n-slate-12">
                  {{ document.name }}
                </span>
              </span>
            </BaseTableCell>

            <BaseTableCell>
              <span class="flex items-center gap-2">
                <Spinner v-if="isBusy(document)" class="size-3" />
                <StudioBadge
                  dot
                  :tone="statusTone(document.status)"
                  :title="
                    document.status === 'failed' ? document.error_message : ''
                  "
                  :data-testid="`status-${document.status}`"
                >
                  {{ statusLabel(document.status) }}
                </StudioBadge>
              </span>
            </BaseTableCell>

            <BaseTableCell>
              <StudioBadge>{{ document.chunk_count }}</StudioBadge>
            </BaseTableCell>

            <BaseTableCell>
              <span class="text-body-main text-n-slate-11">
                {{ formatDate(document.indexed_at) }}
              </span>
            </BaseTableCell>

            <BaseTableCell align="end" class="w-24">
              <div class="flex gap-3 justify-end flex-shrink-0">
                <Button
                  v-tooltip.top="t('AI_AGENT.KNOWLEDGE.TABLE.REINDEX')"
                  icon="i-lucide-refresh-cw"
                  slate
                  sm
                  :disabled="isBusy(document)"
                  data-testid="document-reindex"
                  @click="reindex(document)"
                />
                <Button
                  v-tooltip.top="t('AI_AGENT.KNOWLEDGE.TABLE.DELETE')"
                  icon="i-woot-bin"
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  slate
                  sm
                  data-testid="document-delete"
                  @click="openDeleteDialog(document)"
                />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="$t('AI_AGENT.KNOWLEDGE.TABLE.DELETE_CONFIRM.TITLE')"
      :description="
        $t('AI_AGENT.KNOWLEDGE.TABLE.DELETE_CONFIRM.MESSAGE', {
          name: selectedDocument.name,
        })
      "
      :confirm-button-label="$t('AI_AGENT.KNOWLEDGE.TABLE.DELETE_CONFIRM.YES')"
      :cancel-button-label="$t('AI_AGENT.KNOWLEDGE.TABLE.DELETE_CONFIRM.NO')"
      @confirm="confirmDelete"
    />
  </div>
</template>
